/**
 * <SingleTile> Component
 */
import PropTypes from 'prop-types';
/** @jsx jsx this comment is required for Storybook to render */
import { css, jsx } from '@emotion/core';
import SVG from 'react-inlinesvg';
import {
  breakpoints,
  colors,
  fontFamilies,
  fontSizes,
  fontWeights,
  lineHeights,
  shadows,
  unitConverter as uc,
} from '../../../styles/base';
import Card from '../../atoms/Card/Card';
import Image from '../../atoms/Image/Image';
import sanityImage from '../../../utils/sanityImage';
import { Heading3 } from '../../atoms/Headings/Headings';
import Text from '../../atoms/Text/Text';
import Link from '../../atoms/Link/Link';
import externalLinkIcon from '../../../../static/svg/external-link-icon.svg';
import TileContainer from '../TileContainer/TileContainer';

const SingleTile = ({
  authorTitle,
  clientQuote,
  link,
  quoteAuthor,
  singleTileImage,
}) => {
  const singleTileCss = css`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    height: ${uc('475px')};
    margin-bottom: ${uc('50px')};
    padding: ${uc('0 20px')};
    overflow: hidden;

    &:hover {
      box-shadow: ${link ? shadows.largeHovered : null};
    }
  `;

  const imageCss = css`
    max-height: ${uc('300px')};
    margin-top: ${uc('30px')};

    @media (${breakpoints.tablet}) {
      margin-top: 0;
      transform: scale(0.85);
    }
  `;

  const headingCss = css`
    margin: ${uc('20px 0')};
    font-weight: ${fontWeights.bold};
    line-height: ${lineHeights.copy};
    letter-spacing: 0;
    text-align: center;

    @media (${breakpoints.tablet}) {
      margin: ${uc('0 0 20px 0')};
      font-size: ${fontSizes.eighteen};
    }
  `;

  const authorTextCss = css`
    margin-bottom: ${uc('5px')};
    font-weight: ${fontWeights.medium};
    text-align: center;
  `;

  const linkCss = css`
    color: ${colors.accent};
    font-weight: ${fontWeights.semiBold};
    font-family: ${fontFamilies.proximaSoft};

    &:hover {
      color: ${colors.accentHover};
    }
  `;

  const externalLinkCss = css`
    svg {
      position: relative;
      top: ${uc('2px')};
      width: ${uc('20px')};
      height: ${uc('20px')};
      margin-left: ${uc('5px')};

      .st0 {
        fill: ${colors.accent};
      }
    }
  `;

  return (
    <TileContainer>
      <Card css={singleTileCss}>
        {singleTileImage && (
          <Image
            css={imageCss}
            src={sanityImage(singleTileImage)
              .width(400)
              .fit('max')
              .url()}
            alt={singleTileImage.alt}
          />
        )}
        <Heading3
          css={headingCss}
          fontSize={fontSizes.fourteen}
          color={colors.lightGray.five}
        >
          {clientQuote}
        </Heading3>
        {(quoteAuthor || authorTitle) && (
          <Text
            color={colors.lightGray.five}
            css={authorTextCss}
            fontSize={fontSizes.fourteen}
          >
            <span className="name">{quoteAuthor}</span>
            <span className="role">{authorTitle}</span>
          </Text>
        )}
        {link && link.text && (
          <Link to={link} css={linkCss}>
            {link.text}
            {link.link[0]._type === 'externalLink' && (
              <span css={externalLinkCss}>
                <SVG src={externalLinkIcon} />
              </span>
            )}
          </Link>
        )}
      </Card>
    </TileContainer>
  );
};

SingleTile.propTypes = {
  singleTileImage: PropTypes.shape({ alt: PropTypes.string }),
  clientQuote: PropTypes.string,
  quoteAuthor: PropTypes.string,
  authorTitle: PropTypes.string,
  link: PropTypes.shape([]),
};

SingleTile.defaultProps = {
  singleTileImage: {},
  clientQuote: '',
  quoteAuthor: '',
  authorTitle: '',
  link: false,
};

export default SingleTile;
